import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  OnDestroy,
  ViewChild,
  ElementRef,
  ViewChildren,
} from '@angular/core';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import { Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { LocalStorageService } from './../../../../src/app/services/local-storage.service';
import { getSearchValues } from './search-utils';
import { debounce } from 'lodash';
import {
  GLOBAL_SEARCH_CLICK_RESULT,
  GLOBAL_SEARCH_INPUT_SEARCH,
} from 'src/app/constants/analytics-track-events';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class AppSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchInputToFocus', { static: false })
  searchInputToFocus: ElementRef;
  @ViewChildren('searchTab') searchTab; //contains all the search tab refs
  noResults: string = './assets/no-search.svg';
  searchStates = {
    initial: ['./assets/initial-search.svg', 'Start typing to search'],
    fetching: ['./assets/fetching-search.svg', 'Loading results...'],
    noResults: ['./assets/no-search.svg', 'No results found'],
    dataFound: ['', ''],
    recentSearchPresent: ['', ''],
  }; //search states for showing messages and icons
  externalLinkIcon: string = './assets/external-link-primary.svg'; //external link icon
  defaultUrl = ''; //default url for navigation in new tab
  searchState: string = ''; //search state for showing different icons and messages
  searchTerm = new UntypedFormControl(''); //search input form control
  searchedString: string = ''; //searched string to determine last searched string
  recentSearch: [] = []; //recent search list
  highlightTimeout: any; //highlight search term text timeout
  searchData: any = []; //search data for search term
  inputTimeout: any; //timeout for last saved input string before popup close
  filteredResults: {
    STACK: any[];
    CLUSTER: any[];
    RESOURCE: any[];
    ROUTE: any[];
  } = {
    STACK: [],
    CLUSTER: [],
    RESOURCE: [],
    ROUTE: [],
  }; //different categories of search results
  @Input() ref: any;
  constructor(
    private localStorage: LocalStorageService,
    private router: Router,
  ) {}
  urls = {
    Overview: '',
    'Delivery Pipeline': '/pipeline',
    'Environment Overview': '/overview-v2',
    'Environment Releases': '/releases',
    Configuration: '/configuration',
    misc: 'name',
  };
  tabValues = {
    STACK: { title: 'Projects', selector: 'stackTab' },
    CLUSTER: { title: 'Environments', selector: 'envTab' },
    RESOURCE: { title: 'Resources', selector: 'resourceTab' },
    ROUTE: { title: 'Routes', selector: 'routeTab' },
  };
  /*
    original object with all the types does not guarantee display order. This is declared to maintain order in the UI
  */
  tabOrder: string[] = ['STACK', 'CLUSTER', 'RESOURCE', 'ROUTE'];

  ngOnInit(): void {
    this.searchTerm;
    /*
     * get the data from local storage and set values of searchData, recentSearch and last search
     */
    let store = this.localStorage.getFacetsStore();
    this.searchData = store?.['searchData'];
    this.recentSearch = store?.['recentSearch'] || [];
    this.searchState = this.recentSearch?.length // if recent search is present, show all recent searches, or show empty state
      ? 'recentSearchPresent'
      : 'initial';
  }

  ngAfterViewInit() {
    this.searchInputToFocus?.nativeElement?.focus();
  }

  ngOnDestroy() {
    this.highlightTimeout ? clearTimeout(this.highlightTimeout) : null;
    this.inputTimeout ? clearTimeout(this.inputTimeout) : null;
  }

  /*
   * This function fires when user enters some keyword in the input.
   * Debounce takes care of not firing the search operation on every successive key press
   * This also fetches latest data from local storage when user stops key presses and starts search operation
   */
  processSearch = debounce(() => {
    let store = this.localStorage.getFacetsStore();
    this.searchData = store?.['searchData'];
    this.fetchResults(this.searchTerm?.value, this.searchData);
    triggerAnalyticsEvent(GLOBAL_SEARCH_INPUT_SEARCH);
  }, 300);

  /*
   * This function fetches latest data from local storage when user stops key presses and starts search operation
   */
  fetchResults(searchTerm, searchData) {
    //trim the leading and trailing spaces
    let searchString = searchTerm?.trim()?.toLowerCase();
    let searchValues;
    /*
     * Check if search string still has white space in between words, and separate the words in a list.
     */
    searchValues = searchString?.split(' ');
    /*
     * discard any empty strings from an array
     */
    searchValues = searchValues?.filter((value) => value !== '');
    /*
     * Set the search state and get search results if search string length is greater than 2.
     * Get the filtered results and then sort them according to search type in the filteredResults
     */
    if (searchString?.length > 2 && searchString !== this.searchedString) {
      this.searchState = 'fetching';
      this.searchedString = searchString;
      let results: any = getSearchValues(searchValues, searchData);
      Object.keys(this.filteredResults)?.forEach((key) => {
        this.filteredResults[key] =
          this.getFilteredData(results, 'type', key) || [];
      });
      /* 
        highlight the search results.
      */
      results?.length > 0
        ? this.highlightResults(searchValues, results)
        : (this.searchState = 'noResults');
      this.preSelectTab();
    } else if (
      !(searchString === this.searchedString) ||
      searchString.length < 3
    ) {
      this.searchedString = '';
      this.filteredResults = {
        CLUSTER: [],
        STACK: [],
        RESOURCE: [],
        ROUTE: [],
      };
      this.recentSearch = this.localStorage.getFacetsStore()?.['recentSearch'];
      this.searchState =
        this.localStorage.getFacetsStore()?.['recentSearch']?.length > 0
          ? 'recentSearchPresent'
          : 'initial';
    }
  }

  /*
   * This function is called when user clicks on any search result.
   * First get the local storage object and save the latest search term as recent search
   * If recent search contains more than 5 terms, then discard 5th term and save new one as latest
   * and call constructUrl for construction of url based on newTabNavigation variable.
   */
  navigateTo(event, typeResult, pageType, newTabNavigation = false) {
    event.stopPropagation();
    let store = this.localStorage.getFacetsStore();
    let recentSearch = store?.recentSearch || [];
    store['recentSearch'] = recentSearch?.length
      ? this.setRecentSearchList(recentSearch)
      : [this.searchTerm?.value?.toLowerCase()];
    this.localStorage?.setFacetsStore(store);
    this.constructUrl(typeResult, pageType, newTabNavigation);
    triggerAnalyticsEvent(GLOBAL_SEARCH_CLICK_RESULT);
  }

  /*
    this function finds the first tab with non zero results after search and highlights it.
  */
  preSelectTab() {
    if (this.searchState === 'dataFound') {
      let firstNonEmptyKey: string | undefined;

      for (const key of Object.keys(this.filteredResults)) {
        const value = this.filteredResults[key];
        if (Array.isArray(value) && value.length > 0) {
          firstNonEmptyKey = key;
          break;
        }
      }
      setTimeout(() => {
        let tabArray = this.searchTab?.toArray();
        let tabObject = {
          STACK: this.getFilteredData(tabArray, 'tabTitle', 'Projects'),
          CLUSTER: this.getFilteredData(tabArray, 'tabTitle', 'Environments'),
          RESOURCE: this.getFilteredData(tabArray, 'tabTitle', 'Resources'),
          ROUTE: this.getFilteredData(tabArray, 'tabTitle', 'Routes'),
        };
        /*
          De-highlight the tabs other than the first tab with results after we get fresh result
        */
        Object.keys(tabObject)?.forEach((key) => {
          if (key !== firstNonEmptyKey) {
            tabObject[key][0].activeValue = false;
          }
        });
        /*
          highlight the first tab with results after we get fresh result
        */
        if (tabObject[firstNonEmptyKey][0]) {
          tabObject[firstNonEmptyKey][0].activeValue = true;
        }
      }, 100);
    }
  }

  getFilteredData(data: [], key: string, value: string): any[] {
    return data?.filter((val) => val?.[key] === value) ?? [];
  }

  /*
   * construct the url based on search object type.
   * check if newTab is set to true, that means user has clicked on new tab icon, open the url in new tab
   * Otherwise open the url in the same tab and close search popup
   */
  constructUrl(typeResult, pageType, newTab) {
    this.defaultUrl = newTab
      ? new URL(window.location.href).origin + '/capc'
      : '';
    let constructedUrl = '';
    switch (typeResult?.type) {
      case 'STACK':
        constructedUrl =
          this.defaultUrl +
          '/stack/' +
          typeResult['name'] +
          this.urls[pageType];
        break;
      case 'CLUSTER':
        pageType === 'Environment Releases'
          ? (constructedUrl = `${this.defaultUrl}/stack/${typeResult?.data?.stackName}/releases/cluster/${typeResult?.data?.clusterId}`)
          : (constructedUrl = `${this.defaultUrl}/stack/${typeResult?.data?.stackName}/dialog/cluster/${typeResult?.data?.clusterId}${this.urls[pageType]}`);
        break;
      case 'RESOURCE':
        constructedUrl = typeResult?.data?.branch
          ? `${this.defaultUrl}/stack/${typeResult?.data?.stackName}/blueprint-designer/${typeResult?.data?.branch}/resource-list/dialog/${typeResult?.data?.branch}/resource/type/${typeResult?.data?.resourceType}/name/${typeResult?.name}/overrides/${typeResult?.data?.clusterId}`
          : `${this.defaultUrl}/stack/${typeResult?.data?.stackName}/resources/cluster/${typeResult?.data?.clusterId}/dialog/resource-center/type/${typeResult?.data?.resourceType}/name/${typeResult?.name}/monitoring/${typeResult?.data?.clusterId}`;
        break;
      case 'ROUTE':
        constructedUrl = this.defaultUrl + typeResult?.name;
        break;
    }
    !newTab ? this.ref.close() : null;
    newTab
      ? window.open(constructedUrl, '_blank')
      : this.router.navigate([constructedUrl]);
  }

  setRecentSearchList(recentSearch) {
    recentSearch = recentSearch?.reverse();
    recentSearch?.includes(this.searchTerm?.value.toLowerCase())
      ? (recentSearch = recentSearch.filter(
          (term) => term !== this.searchTerm?.value.toLowerCase(),
        ))
      : null;
    recentSearch.push(this.searchTerm.value.toLowerCase());
    recentSearch?.length > 5 ? recentSearch?.shift() : recentSearch;
    return recentSearch.reverse();
  }

  /*
   * Clear the search input value
   * Set the recent search values from local storage
   */
  clearSearch() {
    this.searchTerm?.setValue('');
    let store = this.localStorage.getFacetsStore();
    this.recentSearch = store?.['recentSearch'] || [];
  }

  /*
   * Settimeout is used to let the ui get the values first before highlighting them
   * First get the all parent element with search item class
   * For each parent element, there is one child searchItemName element which contains env,blueprint,resource,route name
   * highlight the search term in searchItemName content and attach a new highlighted element to the parent element.
   */
  highlightResults(searchValues, results) {
    let key = this.searchTerm.value.toLowerCase();
    this.highlightTimeout = setTimeout(() => {
      let allItemsParent = Array.from(document.querySelectorAll('.searchItem'));
      let allItems = Array.from(document.querySelectorAll('.searchItemName'));
      allItemsParent.forEach((item, index) => {
        const element = allItems?.[index];
        if (searchValues?.length > 0) {
          searchValues?.forEach((val) => {
            this.replaceHighlight(element, val);
          });
        } else {
          this.replaceHighlight(element, key);
        }
      });
    });
    /* 
      set the state to datafound if search results are present, otherwise show error message
    */
    this.searchState = results?.length > 0 ? 'dataFound' : 'noResults';
  }

  replaceHighlight(textElem, val) {
    const regex = new RegExp(val, 'gi');
    textElem.innerHTML = textElem?.innerHTML?.replace(
      regex,
      `<mark>${val}</mark>`,
    );
  }

  onSearch(event) {
    event.preventDefault();
  }
}
